"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import { getRecentStores, saveRecentStore } from "@/lib/storePickerHelpers";
import { parseStreet, groupByStreet } from "@/lib/taiwanStores";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { useFocusTrap } from "@/lib/useFocusTrap";

const StoreMap = dynamic(() => import("./StoreMap"), { ssr: false });

const C = {
  textPrimary: "#1a1a1a",
  textSecondary: "#4a4a4a",
  textMuted: "#6b6b6b",
  border: "#cfcfcf",
  borderStrong: "#7a7a7a",
  bgSoft: "#f5f5f5",
  accent: "#b45309",
  accentBg: "#fff7ed",
  red: "#d84315",
  blue: "#1565c0",
  blueBg: "#e3f2fd",
};

const MODE = { HIERARCHY: "hierarchy", NEAR: "near" };

export default function StorePickerModal({ isOpen, onClose, onSelect }) {
  const [regions, setRegions] = useState(null);
  const [mode, setMode] = useState(MODE.HIERARCHY);
  const [city, setCity] = useState("");
  const [town, setTown] = useState("");
  const [street, setStreet] = useState("");
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle");
  const [gpsError, setGpsError] = useState("");
  const [showAllStreets, setShowAllStreets] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchFallback, setSearchFallback] = useState(false);
  const [showPermHelp, setShowPermHelp] = useState(false);
  // true = 下次 stores 更新時地圖 auto-fit 到 bounds(切縣市/搜尋/定位後需要)
  // false = 保持用戶視角(拖動地圖後 refetch 時,不能 fit 不然會 loop)
  const [shouldAutoFit, setShouldAutoFit] = useState(true);
  const panelRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const mapMoveDebounceRef = useRef(null);

  useBodyScrollLock(isOpen);
  useFocusTrap({ isActive: isOpen, containerRef: panelRef, onEscape: onClose });

  useEffect(() => {
    if (!isOpen) return;
    setRecent(getRecentStores());
    if (!regions) {
      fetch("/api/cvs-stores/regions")
        .then((r) => r.json())
        .then((d) => setRegions(d.cities || []))
        .catch(() => setRegions([]));
    }
  }, [isOpen, regions]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode !== MODE.HIERARCHY) return;
    if (!city || !town) {
      setStores([]);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ city, town, limit: "200" });
        const res = await fetch(`/api/cvs-stores/search?${params}`);
        const data = await res.json();
        if (!cancelled)
          setStores(Array.isArray(data.stores) ? data.stores : []);
      } catch {
        if (!cancelled) setStores([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, city, town]);

  // 地址/店名 free-text 搜尋 — debounce 300ms 避免每打 1 字打 API。
  // 2 字以下回到 hierarchy 模式;2 字以上切 SEARCH 模式覆蓋列表。
  useEffect(() => {
    if (!isOpen) return;
    const q = searchText.trim();
    if (q.length < 2) {
      if (mode === MODE.SEARCH) {
        setMode(MODE.HIERARCHY);
        setStores([]);
        setSearchFallback(false);
      }
      return;
    }
    setSearching(true);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: "50" });
        const res = await fetch(`/api/cvs-stores/search?${params}`);
        const data = await res.json();
        setShouldAutoFit(true); // 搜尋新結果 → 地圖 fit 到新結果位置
        setStores(Array.isArray(data.stores) ? data.stores : []);
        // 後端若找不到精確地址會 geocode 改撈附近,帶 fallback:'nearby'
        setSearchFallback(data.fallback === "nearby");
        setMode(MODE.SEARCH);
        setCity("");
        setTown("");
        setStreet("");
        setSelectedId(null);
      } catch {
        setStores([]);
        setSearchFallback(false);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // `mode` 刻意不放 deps:我們 set mode=SEARCH 會觸發 re-run 多打一次 API。
    // mode 只在這 effect 內部讀取決定要不要切回 HIERARCHY,不驅動 re-fetch。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchText]);

  const townsOfCity = useMemo(() => {
    if (!city || !regions) return [];
    return regions.find((c) => c.name === city)?.towns ?? [];
  }, [city, regions]);

  const streets = useMemo(() => {
    if (mode !== MODE.HIERARCHY || !city || !town) return [];
    return groupByStreet(stores);
  }, [mode, stores, city, town]);

  const visibleStores = useMemo(() => {
    if (mode === MODE.NEAR) return stores;
    if (!street) return stores;
    return stores.filter(
      (s) => parseStreet(s.address, s.city, s.town) === street,
    );
  }, [mode, stores, street]);

  const selectedStore = useMemo(() => {
    if (!selectedId) return null;
    return (
      [...stores, ...recent].find((s) => s.store_id === selectedId) || null
    );
  }, [selectedId, stores, recent]);

  const handleCityChange = (e) => {
    setShouldAutoFit(true); // 切縣市 → 地圖要 fit 到新區
    setCity(e.target.value);
    setTown("");
    setStreet("");
    setSelectedId(null);
  };

  const handleTownChange = (e) => {
    setShouldAutoFit(true);
    setTown(e.target.value);
    setStreet("");
    setSelectedId(null);
  };

  const handleStreetPick = (name) => {
    setShouldAutoFit(true);
    setStreet(name);
    setSelectedId(null);
  };

  // 選新城市/鄉鎮時把路段摺疊回 Top N
  useEffect(() => {
    setShowAllStreets(false);
  }, [city, town]);

  const handleReset = () => {
    setShouldAutoFit(true);
    setMode(MODE.HIERARCHY);
    setCity("");
    setTown("");
    setStreet("");
    setStores([]);
    setSelectedId(null);
    setGpsStatus("idle");
    setSearchText("");
  };

  const handleNearbyClick = async () => {
    setGpsError("");
    if (!("geolocation" in navigator)) {
      setGpsStatus("unavailable");
      return;
    }
    // 先查權限狀態:denied 代表用戶之前拒過,瀏覽器不會再彈 prompt,
    // 直接跳教學彈窗(不然 getCurrentPosition 會秒 fail 用戶一頭霧水)。
    if (navigator.permissions?.query) {
      try {
        const perm = await navigator.permissions.query({ name: "geolocation" });
        if (perm.state === "denied") {
          setShowPermHelp(true);
          return;
        }
      } catch {
        // 某些 browser 不支援 Permissions API for geolocation,fall through 走原本流程
      }
    }
    setGpsStatus("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setShouldAutoFit(true); // 定位到新位置 → 地圖要 fit 到附近門市
        setMode(MODE.NEAR);
        setCity("");
        setTown("");
        setStreet("");
        setSelectedId(null);
        setLoading(true);
        try {
          const params = new URLSearchParams({
            lat: String(latitude),
            lng: String(longitude),
            limit: "10",
          });
          const res = await fetch(`/api/cvs-stores/nearest?${params}`);
          const data = await res.json();
          setStores(Array.isArray(data.stores) ? data.stores : []);
          setGpsStatus("idle");
        } catch {
          setStores([]);
          setGpsStatus("idle");
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        // 分清楚三種失敗原因給用戶看得懂的訊息
        const msg =
          err.code === 1
            ? "您拒絕了位置權限。可在瀏覽器網址列旁的鎖頭開啟，或改用下方縣市選擇"
            : err.code === 2
              ? "無法取得位置（裝置訊號弱），請改用下方縣市選擇"
              : err.code === 3
                ? "定位超時，請重試或改用下方縣市選擇"
                : "定位失敗，請改用下方縣市選擇";
        setGpsStatus("error");
        setGpsError(msg);
      },
      // enableHighAccuracy: true 讓手機走 GPS(精準到 5-20m)、桌機也
      // 會用 Wi-Fi AP 位置資料庫而非 IP(IP geolocation 常偏十幾 km)。
      // maximumAge: 30s — 舊版 5 分鐘容易拿到不準的快取位置。
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  // 點卡片/地圖 pin 都只高亮、不直接送出 — 下方 sticky confirm bar 才是送出
  const handleStoreHighlight = useCallback((store) => {
    setSelectedId(store.store_id);
    document
      .getElementById(`store-${store.store_id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  // 用戶拖動 / 縮放地圖停下 → 用新 center 呼叫 nearest API 重抓附近門市。
  // debounce 500ms 避免連續拖動 + 縮放組合打爆 API。
  // shouldAutoFit=false 讓 StoreMap 不要 fitBounds 拉回鏡頭(否則 fitBounds
  // 會 fire zoomend → 又觸發這個 handler → 無限 loop + modal 崩)。
  const handleMapMoveEnd = useCallback(({ lat, lng }) => {
    clearTimeout(mapMoveDebounceRef.current);
    mapMoveDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          lat: String(lat),
          lng: String(lng),
          limit: "10",
        });
        const res = await fetch(`/api/cvs-stores/nearest?${params}`);
        const data = await res.json();
        if (Array.isArray(data.stores)) {
          setShouldAutoFit(false); // ← 關鍵:用戶在控制地圖,別動鏡頭
          setStores(data.stores);
          setMode(MODE.NEAR);
          // 切 NEAR 模式,清其他流程狀態避免 UI 混亂
          setCity("");
          setTown("");
          setStreet("");
          setSearchText("");
          setSearchFallback(false);
          setSelectedId(null);
        }
      } catch {
        /* silent — 保留既有結果 */
      }
    }, 500);
  }, []);

  const handleConfirm = useCallback(() => {
    const allStores = [...stores, ...recent];
    const store = allStores.find((s) => s.store_id === selectedId);
    if (!store) return;
    saveRecentStore(store);
    onSelect?.({
      storeId: store.store_id,
      storeName: store.store_name,
      address: store.address,
    });
    onClose?.();
  }, [selectedId, stores, recent, onSelect, onClose]);

  if (!isOpen) return null;

  const inNearMode = mode === MODE.NEAR;
  const inSearchMode = mode === MODE.SEARCH;
  const hierarchyPickedAll = city && town && street;
  const mapStores = visibleStores.length > 0 ? visibleStores : recent;
  const showMap =
    inNearMode || inSearchMode || (city && town) || recent.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="選擇 7-11 門市"
      className="fixed inset-0 z-50 flex items-stretch md:items-center md:justify-center"
      style={{ backgroundColor: "rgba(0,0,0,.55)" }}
      // 只有直接點「黑色背景」才關閉 modal。
      // 原本 onClick={onClose} 會吃到「地圖拖動起點在內、終點在外」這類
      // 跨元素 click,瀏覽器把 target 判定到 backdrop → 誤關 modal。
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full md:max-w-2xl md:max-h-[90vh] md:rounded-2xl lg:max-w-3xl lg:h-[85vh] shadow-2xl overflow-hidden h-dvh"
        style={{
          backgroundColor: "#ffffff",
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          overscrollBehavior: "contain",
        }}
      >
        {/* Header sticky 在頂 — close 按鈕永遠可按 */}
        <div
          className="flex items-center justify-between px-4 py-3 shrink-0 sticky top-0 z-10"
          style={{
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: "#ffffff",
          }}
        >
          <h2 className="text-lg font-bold" style={{ color: C.textPrimary }}>
            選擇 7-11 門市
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="w-11 h-11 flex items-center justify-center rounded-full -mr-2"
            style={{ color: C.textPrimary }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Main:
            mobile  → flex-col stack (filter → map → list)
            desktop → CSS grid 55% | 45%, map 左全高、filter 右上、list 右下 */}
        <div className="flex-1 overflow-y-auto">
          {/* === Filter 區（整合 GPS + selects + streets + reset） === */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            {/* GPS */}
            <div className="px-4 pt-3">
              <button
                type="button"
                onClick={handleNearbyClick}
                disabled={gpsStatus === "locating"}
                className="w-full flex items-center justify-center gap-2 rounded-lg font-semibold disabled:opacity-60"
                style={{
                  minHeight: "48px",
                  fontSize: "15px",
                  color: inNearMode ? "#fff" : C.blue,
                  backgroundColor: inNearMode ? C.blue : C.blueBg,
                  border: `1.5px solid ${C.blue}`,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="10" r="3" />
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                </svg>
                {gpsStatus === "locating"
                  ? "定位中…"
                  : inNearMode
                    ? "已顯示附近 10 家"
                    : "📍 顯示我附近的門市"}
              </button>
              {gpsStatus === "error" && gpsError && (
                <p
                  className="mt-2 text-sm text-center"
                  style={{ color: C.red }}
                >
                  {gpsError}
                </p>
              )}
              {gpsStatus === "unavailable" && (
                <p
                  className="mt-2 text-sm text-center"
                  style={{ color: C.red }}
                >
                  這個瀏覽器不支援定位
                </p>
              )}
              {(gpsStatus === "error" || gpsStatus === "unavailable") && (
                <div className="mt-1 text-center">
                  <button
                    type="button"
                    onClick={() => setShowPermHelp(true)}
                    className="text-sm underline"
                    style={{ color: C.blue }}
                  >
                    怎麼打開定位?
                  </button>
                </div>
              )}
            </div>

            {/* 地址/店名搜尋 — 比下拉選單更快找到門市 */}
            <div className="px-4 pt-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="🔍 直接輸入地址、路名或店名(2 字以上)"
                  className="w-full rounded-lg px-3 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                  style={{
                    // 16px 防 iOS Safari focus 時 auto-zoom 整頁放大
                    fontSize: "16px",
                    height: "44px",
                    color: C.textPrimary,
                    backgroundColor: "#ffffff",
                    border: `1.5px solid ${searchText ? C.accent : C.borderStrong}`,
                    paddingRight: searchText ? "2.5rem" : "0.75rem",
                  }}
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => setSearchText("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full"
                    aria-label="清除搜尋"
                    style={{ color: C.textMuted }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {searching && (
                <p className="mt-1.5 text-xs" style={{ color: C.textMuted }}>
                  搜尋中…
                </p>
              )}
              {inSearchMode && !searching && stores.length === 0 && (
                <p className="mt-1.5 text-xs" style={{ color: C.textMuted }}>
                  找不到符合的門市,試試其他關鍵字或改用下方縣市選擇
                </p>
              )}
              {inSearchMode && !searching && stores.length > 0 && (
                <p className="mt-1.5 text-xs" style={{ color: C.textMuted }}>
                  {searchFallback
                    ? `該地址沒直接門市,顯示附近 ${stores.length} 家 7-11`
                    : `找到 ${stores.length} 家,點卡片可選取`}
                </p>
              )}
            </div>

            {/* 縣市 + 鄉鎮 + 路段 */}
            {!inNearMode && !inSearchMode && (
              <div className="px-4 py-3 space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={city}
                    onChange={handleCityChange}
                    aria-label="縣市"
                    className="rounded-lg px-3 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 font-medium"
                    style={{
                      fontSize: "16px",
                      height: "48px",
                      color: C.textPrimary,
                      backgroundColor: "#ffffff",
                      border: `1.5px solid ${city ? C.accent : C.borderStrong}`,
                    }}
                  >
                    <option value="">1. 選縣市</option>
                    {regions?.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}（{c.count}）
                      </option>
                    ))}
                  </select>
                  <select
                    value={town}
                    onChange={handleTownChange}
                    disabled={!city}
                    aria-label="鄉鎮區"
                    className="rounded-lg px-3 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 font-medium disabled:cursor-not-allowed"
                    style={{
                      fontSize: "16px",
                      height: "48px",
                      color: city ? C.textPrimary : C.textMuted,
                      backgroundColor: city ? "#ffffff" : C.bgSoft,
                      border: `1.5px solid ${town ? C.accent : city ? C.borderStrong : C.border}`,
                    }}
                  >
                    <option value="">2. 選鄉鎮區</option>
                    {townsOfCity.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 路段 tag — 預設 Top 10 熱門，按「更多」展開全部 */}
                {city &&
                  town &&
                  streets.length > 0 &&
                  (() => {
                    const TOP_N = 10;
                    const visibleStreets = showAllStreets
                      ? streets
                      : streets.slice(0, TOP_N);
                    const hasMore = !showAllStreets && streets.length > TOP_N;
                    return (
                      <div>
                        <p
                          className="text-sm font-semibold mt-1 mb-2"
                          style={{ color: C.textSecondary }}
                        >
                          3. 選路段（{streets.length} 條）
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {visibleStreets.map((s) => {
                            const active = s.name === street;
                            return (
                              <button
                                key={s.name}
                                type="button"
                                onClick={() => handleStreetPick(s.name)}
                                className="rounded-full px-3 transition-colors"
                                style={{
                                  minHeight: "36px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: active ? "#fff" : C.textPrimary,
                                  backgroundColor: active
                                    ? C.accent
                                    : C.accentBg,
                                  border: `1px solid ${active ? C.accent : C.border}`,
                                }}
                              >
                                {s.name}{" "}
                                <span style={{ opacity: 0.7 }}>
                                  ({s.count})
                                </span>
                              </button>
                            );
                          })}
                          {hasMore && (
                            <button
                              type="button"
                              onClick={() => setShowAllStreets(true)}
                              className="rounded-full px-3 underline"
                              style={{
                                minHeight: "36px",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: C.blue,
                                backgroundColor: "transparent",
                                border: `1px dashed ${C.blue}`,
                              }}
                            >
                              + 其他 {streets.length - TOP_N} 條
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {(city || town || street) && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="underline"
                    style={{ color: C.textSecondary, fontSize: "13px" }}
                  >
                    重新選擇
                  </button>
                )}
              </div>
            )}
          </div>

          {/* === 地圖區 === mobile: 中間 stack；desktop: 左欄全高 */}
          {showMap && (
            <div
              className="relative h-[220px] md:h-[260px] overflow-hidden"
              style={{ backgroundColor: C.bgSoft }}
            >
              <div className="w-full h-full">
                <StoreMap
                  stores={mapStores}
                  selectedId={selectedId}
                  onSelect={handleStoreHighlight}
                  onMoveEnd={handleMapMoveEnd}
                  shouldAutoFit={shouldAutoFit}
                />
              </div>
            </div>
          )}

          {/* === 清單區 === mobile: 最下 flex-1；desktop: 右下 scroll */}
          <div className="px-4 py-3" style={{ backgroundColor: "#ffffff" }}>
            {!inNearMode && !inSearchMode && !city && recent.length === 0 && (
              <div className="py-10 text-center">
                <p
                  className="mb-1 font-semibold"
                  style={{ color: C.textPrimary, fontSize: "17px" }}
                >
                  點上方「附近的門市」或選縣市
                </p>
                <p style={{ color: C.textMuted, fontSize: "14px" }}>
                  輸入地址、定位、或選縣市 → 鄉鎮區 → 路段,都能找到門市
                </p>
              </div>
            )}

            {!inNearMode && city && town && !street && (
              <p
                className="py-4 text-center"
                style={{ color: C.textSecondary, fontSize: "14px" }}
              >
                {loading
                  ? "載入中…"
                  : `${city}${town} 共 ${stores.length} 家，選上方路段 tag 或點地圖圖釘`}
              </p>
            )}

            {!inNearMode && hierarchyPickedAll && (
              <>
                <ListHeading
                  title={`${street} 共 ${visibleStores.length} 家`}
                />
                <ul className="space-y-2">
                  {visibleStores.map((s) => (
                    <StoreRow
                      key={s.store_id}
                      store={s}
                      selected={s.store_id === selectedId}
                      onClick={() => handleStoreHighlight(s)}
                    />
                  ))}
                </ul>
              </>
            )}

            {inNearMode && (
              <>
                <ListHeading
                  title={
                    loading
                      ? "搜尋附近…"
                      : `附近 ${stores.length} 家，按距離排序`
                  }
                />
                <ul className="space-y-2">
                  {stores.map((s) => (
                    <StoreRow
                      key={s.store_id}
                      store={s}
                      selected={s.store_id === selectedId}
                      onClick={() => handleStoreHighlight(s)}
                      distance={s.distance_m}
                    />
                  ))}
                </ul>
              </>
            )}

            {inSearchMode && stores.length > 0 && (
              <>
                <ListHeading
                  title={
                    searchFallback
                      ? `附近 ${stores.length} 家(依地址找周邊)`
                      : `搜尋結果 ${stores.length} 家`
                  }
                />
                <ul className="space-y-2">
                  {stores.map((s) => (
                    <StoreRow
                      key={s.store_id}
                      store={s}
                      selected={s.store_id === selectedId}
                      onClick={() => handleStoreHighlight(s)}
                    />
                  ))}
                </ul>
              </>
            )}

            {!inNearMode && !inSearchMode && !city && recent.length > 0 && (
              <section>
                <h3
                  className="text-sm font-semibold mb-2"
                  style={{ color: C.textSecondary }}
                >
                  最近使用
                </h3>
                <ul className="space-y-2">
                  {recent.map((s) => (
                    <StoreRow
                      key={`recent-${s.store_id}`}
                      store={s}
                      onClick={() => handleStoreHighlight(s)}
                      badge="最近"
                    />
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>

        {/* Sticky 底部確認 bar — 選中商家才顯示，Main scroll 不會影響它 */}
        {selectedStore && (
          <div
            className="shrink-0 px-4 py-3"
            style={{
              backgroundColor: "#ffffff",
              borderTop: `1px solid ${C.border}`,
              boxShadow: "0 -4px 12px rgba(0,0,0,.06)",
            }}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p
                  className="font-bold truncate"
                  style={{ color: C.textPrimary, fontSize: "15px" }}
                >
                  已選：{selectedStore.store_name}
                </p>
                <p
                  className="truncate"
                  style={{ color: C.textMuted, fontSize: "12px" }}
                >
                  {selectedStore.address}
                </p>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="shrink-0 px-5 rounded-lg font-bold"
                style={{
                  minHeight: "48px",
                  fontSize: "15px",
                  color: "#fff",
                  backgroundColor: C.accent,
                  border: "none",
                }}
              >
                確認選這家
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 定位權限教學彈窗 — 用戶之前拒過 / 不支援時顯示分平台步驟 */}
      {/* z-index 要比 Leaflet 地圖的 .leaflet-control(z-index:1000) 高,
          不然彈窗會被地圖 tile/control 壓住看不到教學內容 */}
      {showPermHelp && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowPermHelp(false)}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-lg font-bold"
                style={{ color: C.textPrimary }}
              >
                怎麼打開定位
              </h3>
              <button
                type="button"
                onClick={() => setShowPermHelp(false)}
                aria-label="關閉"
                className="w-9 h-9 flex items-center justify-center rounded-full"
                style={{ color: C.textPrimary }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p className="mb-3 text-sm" style={{ color: C.textSecondary }}>
              你可能之前點過「不允許」,或手機沒給瀏覽器定位權限。以下是各裝置的開啟方式:
            </p>
            <div className="text-sm space-y-3">
              <div>
                <p
                  className="font-semibold mb-1"
                  style={{ color: C.textPrimary }}
                >
                  📱 iPhone(Safari)
                </p>
                <p style={{ color: C.textSecondary }}>
                  設定 → 隱私與安全性 → 定位服務 → 網站網站 → Safari →
                  改「允許」或「詢問」
                </p>
              </div>
              <div>
                <p
                  className="font-semibold mb-1"
                  style={{ color: C.textPrimary }}
                >
                  📱 iPhone(Chrome)
                </p>
                <p style={{ color: C.textSecondary }}>
                  設定 → Chrome → 位置 → 改「允許」
                </p>
              </div>
              <div>
                <p
                  className="font-semibold mb-1"
                  style={{ color: C.textPrimary }}
                >
                  🤖 Android
                </p>
                <p style={{ color: C.textSecondary }}>
                  點網址列旁的鎖頭 / ⓘ → 權限 → 位置 → 允許
                </p>
              </div>
              <div>
                <p
                  className="font-semibold mb-1"
                  style={{ color: C.textPrimary }}
                >
                  💻 電腦
                </p>
                <p style={{ color: C.textSecondary }}>
                  點網址列左邊鎖頭 → 網站設定 → 位置 → 改「允許」或「詢問」
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs" style={{ color: C.textMuted }}>
              改完後重新整理頁面再試一次。不想開定位也沒關係,用上方「地址搜尋」或下方「縣市選擇」都找得到門市。
            </p>
            <button
              type="button"
              onClick={() => setShowPermHelp(false)}
              className="mt-4 w-full rounded-lg py-2.5 font-semibold"
              style={{ backgroundColor: C.blue, color: "#fff" }}
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ListHeading({ title }) {
  return (
    <div
      className="flex items-center gap-2 mb-3 pb-2"
      style={{ borderBottom: `2px solid #b45309` }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#b45309"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
      <h3
        className="font-bold flex-1"
        style={{ color: "#1a1a1a", fontSize: "15px" }}
      >
        或直接從清單點選
      </h3>
      <span style={{ color: "#6b6b6b", fontSize: "12px" }}>{title}</span>
    </div>
  );
}

function StoreRow({ store, selected, onClick, badge, distance }) {
  const services = Array.isArray(store.services) ? store.services : [];
  const is24h = store.op_time === "24H";
  const distText = Number.isFinite(distance)
    ? distance < 1000
      ? `${distance} m`
      : `${(distance / 1000).toFixed(1)} km`
    : null;
  return (
    <li id={`store-${store.store_id}`}>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left rounded-xl px-4 py-3 transition-colors"
        style={{
          minHeight: "68px",
          backgroundColor: selected ? "#fff3e0" : "#ffffff",
          border: `2px solid ${selected ? "#d84315" : "#cfcfcf"}`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-bold"
                style={{ color: "#1a1a1a", fontSize: "17px" }}
              >
                {store.store_name}
              </span>
              {distText && (
                <span
                  className="px-1.5 py-0.5 rounded font-semibold"
                  style={{
                    backgroundColor: "#1565c0",
                    color: "#fff",
                    fontSize: "11px",
                  }}
                >
                  📍 {distText}
                </span>
              )}
              {badge && (
                <span
                  className="px-1.5 py-0.5 rounded font-semibold"
                  style={{
                    backgroundColor: "#b45309",
                    color: "#fff",
                    fontSize: "11px",
                  }}
                >
                  {badge}
                </span>
              )}
              {is24h && (
                <span
                  className="px-1.5 py-0.5 rounded font-semibold"
                  style={{
                    backgroundColor: "#c8e6c9",
                    color: "#1b5e20",
                    fontSize: "11px",
                  }}
                >
                  24H
                </span>
              )}
            </div>
            <p className="mt-1" style={{ color: "#4a4a4a", fontSize: "14px" }}>
              {store.address}
            </p>
            {services.length > 0 && (
              <p
                className="mt-1 truncate"
                style={{ color: "#6b6b6b", fontSize: "12px" }}
              >
                {services.slice(0, 5).join("・")}
              </p>
            )}
          </div>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            className="shrink-0 mt-1"
            style={{ color: "#4a4a4a" }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </button>
    </li>
  );
}
